/// <reference types="@angular/localize" />

import {
  enableProdMode,
  importProvidersFrom,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { environment } from '@environments/environment';
import { AppComponent } from './app/app.component';
import { provideAnimations } from '@angular/platform-browser/animations';
import { APP_ROUTES } from './app/app-routing';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  PreloadAllModules,
  provideRouter,
  TitleStrategy,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogModule,
} from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAuth } from 'angular-auth-oidc-client';
import { Apollo, provideNamedApollo } from 'apollo-angular';
import { InMemoryCache } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import {
  AuthInterceptor,
  PAGINATION_CONFIG,
  AvatarColorPipe,
  FILTER_COMPONENT_CONFIG,
  SKIP_ERROR_HANDLING_OPERATIONS_LIST,
  defaultMatDialogConfig,
  CustomTitleStrategy,
  THEME_PERSISTENCE_ADAPTER,
} from '@tiltgroup/web-components';
import { UserSettingsThemeAdapter } from './app/shared/services/user-settings-theme.adapter';
import { GraphqlErrorsInterceptor } from '@shared/interceptors/graphql-errors.interceptor';
import { SqlOperator } from '@graphql-nexus-types';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';
import { DecimalPipe } from '@angular/common';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import * as Sentry from '@sentry/angular';
import { initSentry, provideSentry } from '@shared/sentry';
import { isEmbedPage } from '@shared/embed/embed-protocol';
import { provideEmbedAuth } from '@shared/embed/embed-auth.providers';
import { initHotjar } from '@shared/hotjar';

initSentry();

if (environment.production) {
  enableProdMode();
}

initHotjar(environment.hotjarSiteId);

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

// Framed by another page, which hands over tokens; the login redirect can't run in a frame.
const embedded = isEmbedPage();

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    { provide: THEME_PERSISTENCE_ADAPTER, useClass: UserSettingsThemeAdapter },
    AvatarColorPipe,
    MatSnackBar,
    Apollo,
    provideAuth({
      config: {
        authority: environment.sso.url,
        postLoginRoute: `/`,
        forbiddenRoute: '/forbidden',
        unauthorizedRoute: '/unauthorized',
        disableIatOffsetValidation: true,
        historyCleanupOff: true,
        redirectUrl: `${window.location.origin}/callback`,
        postLogoutRedirectUri: window.location.origin,
        clientId: environment.sso.clientId,
        responseType: 'code',
        scope: 'profile email permissions organizations openid',
        useRefreshToken: !embedded,
        renewTimeBeforeTokenExpiresInSeconds: 30,
        silentRenew: !embedded,
        ignoreNonceAfterRefresh: true, // this is required if the id_token is not returned
        triggerRefreshWhenIdTokenExpired: false, // required when refreshing the browser if id_token is not updated after the first authentication
        allowUnsafeReuseRefreshToken: true, // this is required if the refresh token is not rotated
        autoUserInfo: false, // if the user endpoint is not supported
        secureRoutes: [
          environment.graphQLURL,
          environment.apiURL,
          environment.commodityServiceAPIURL,
        ],
        disablePkce: true,
      },
    }),
    ...(embedded ? provideEmbedAuth() : []),
    {
      provide: TitleStrategy,
      useClass: CustomTitleStrategy,
    },
    provideRouter(
      APP_ROUTES,
      inMemoryScrollingFeature,
      withPreloading(PreloadAllModules),
      // Angular 22 made 'always' the default, so child routes inherit the
      // parent's `overridePreviousTitle` and CustomTitleStrategy drops the
      // "Shipment: :id" prefix from the tab title.
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })
    ),
    importProvidersFrom(BrowserModule, MatDialogModule),
    provideAnimations(),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: defaultMatDialogConfig,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GraphqlErrorsInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: SKIP_ERROR_HANDLING_OPERATIONS_LIST,
      useValue: [
        'IntercomMessengerToken',
        'ValidateLoad',
        'UpdateLoadFromManager',
        'BulkUpdateLoads',
        'CreateLoads',
        'CreateCarrierVehicle',
        'UpdateCarrierVehicle',
        // The share modal shows its own message and keeps the documents still to send.
        'ShareDocumentsForSignature',
        // NEX-5173: the lane pricing page reads a stored lane by id and already falls
        // back when it is missing - empty state from the URL, re-pricing from a Recent
        // lanes chip - so the backend's not-found text must not reach the user.
        'LanePricing',
        'BrokerDocumentSettings',
        // The assistant shows its own errors in place, in the panel and its views.
        'AiAssistantAllowance',
        'AiAssistantConversations',
        'AiAssistantConversation',
        'AiAssistantOlderMessages',
        'SendAiAssistantMessage',
        'DecideAiAssistantApprovals',
        'StopAiAssistantRun',
        'DeleteAiAssistantConversations',
        'AiAssistantWatches',
        'CancelAiAssistantWatch',
      ],
    },
    {
      provide: FILTER_COMPONENT_CONFIG,
      useValue: {
        operatorMap: {
          [SqlOperator.Eq]: 'equals',
          [SqlOperator.Neq]: 'does not equal',
          [SqlOperator.Gt]: 'greater than',
          [SqlOperator.Gte]: 'greater than or equals',
          [SqlOperator.Lt]: 'less than',
          [SqlOperator.Lte]: 'less than or equals',
          [SqlOperator.Like]: 'contains',
          [SqlOperator.NotLike]: 'does not contain',
          [SqlOperator.Ilike]: 'contains',
          [SqlOperator.NotIlike]: 'does not contain',
          [SqlOperator.StartsWith]: 'starts with',
          [SqlOperator.EndWith]: 'ends with',
          [SqlOperator.In]: 'is one of',
          [SqlOperator.NotIn]: 'is not one of',
          [SqlOperator.Between]: 'between',
          [SqlOperator.NotBetween]: 'not between',
          [SqlOperator.IsNull]: 'is not set',
          [SqlOperator.IsNotNull]: 'is set',
        },
        uniqueOperators: [SqlOperator.IsNull, SqlOperator.IsNotNull],
      },
    },
    {
      provide: PAGINATION_CONFIG,
      useValue: {
        paginationKey: 'paginatorInfo',
        currentPage: 'currentPage',
        lastPage: 'lastPage',
        total: 'total',
      },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'always',
        subscriptSizing: 'dynamic',
      },
    },
    provideNamedApollo(() => {
      const httpLink = inject(HttpLink);

      /**
       * NEX-162 cache policy:
       * - DEFAULT: `no-cache`. Freight/business data (loads, shipments, bids,
       *   tasks, notifications, …) is volatile and paginated; tables are kept
       *   fresh by Pusher events mutating local state, not by the Apollo cache.
       * - EXCEPTIONS: stable reference data opts into `cache-first` by
       *   spreading `ApolloCacheService.cachedQueryOptions('<Domain>')` into
       *   the query call (domains and TTL are defined in
       *   `@services/queries/cache-policies.ts`).
       * - INVALIDATION: mutation-driven eviction through `ApolloCacheService`
       *   (`@shared/services/apollo-cache.service.ts`) plus a 5-minute TTL
       *   enforced by `cachedQueryOptions`; Pusher-driven eviction is
       *   available via `ApolloCacheService.wireChannelInvalidation`.
       * - Full decision record: docs/adr/NEX-162-apollo-cache-strategy.md
       */
      return {
        default: {
          cache: new InMemoryCache({
            typePolicies: {
              TableSettingPaginator: {
                keyFields: ['data'],
              },
              TableSetting: {
                keyFields: ['name', 'sort', 'columns', ['name']],
              },
            },
          }),
          link: httpLink.create({
            uri: (operation) =>
              `${environment.graphQLURL}?op=${encodeURIComponent(
                operation.operationName || 'anonymous'
              )}`,
            extractFiles: (body) => extractFiles(body, isExtractableFile),
          }),
          defaultOptions: {
            watchQuery: {
              fetchPolicy: 'no-cache',
              errorPolicy: 'all',
            },
            query: {
              fetchPolicy: 'no-cache',
              errorPolicy: 'all',
            },
          },
        },
      };
    }),
    provideNgxSkeletonLoader({
      // ngx-skeleton-loader (v12/v13) spreads the whole root theme into its `[style]` binding,
      // including the `extendsFromRoot` flag, which Angular 22 reports as NG0318. A non-enumerable
      // flag still enables merging (the library only reads it) but is skipped by the spread.
      theme: Object.defineProperty(
        {
          'border-radius': '4px',
          'background-color': 'var(--twc-surface-skeleton-loader)',
          margin: 0,
          opacity: 0.5,
        },
        'extendsFromRoot',
        { value: true, enumerable: false }
      ),
    }),
    DecimalPipe,
    // Last on purpose: `importProvidersFrom(BrowserModule)` registers Angular's
    // default ErrorHandler, and the last provider for a token wins.
    provideSentry(),
  ],
}).catch((err) => {
  Sentry.captureException(err);
  console.error(err);
});
